import { useEffect, useMemo, useRef, useState } from "react";
import { MENU_GROUPS as SEED_GROUPS } from "@/lib/menu-data";
import { fetchMenuTree, absoluteUrl, listSiteContent } from "@/lib/api";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Utensils, Coffee, Wine, ChefHat, Heart, Sparkles, ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut } from "lucide-react";
import HeroSlideshow from "@/components/HeroSlideshow";
import { useSettings } from "@/context/SettingsContext";
import MenuPdfBook from "@/components/MenuPdfBook";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

const GROUP_ICONS = {
  food: Utensils,
  beverages: Coffee,
  wine: Wine,
};

// Pick a lucide icon based on the group label
function iconForGroup(label = "") {
  const key = label.toLowerCase();
  if (key.includes("bever") || key.includes("drink") || key.includes("tea") || key.includes("coffee")) return Coffee;
  if (key.includes("wine") || key.includes("spirit") || key.includes("cocktail")) return Wine;
  return Utensils;
}

function ItemFlags({ item }) {
  const flags = [];
  if (item.chef_recommended) flags.push({ icon: ChefHat, label: "Chef's Rec", tid: "flag-chef" });
  if (item.favorite) flags.push({ icon: Heart, label: "Favorite", tid: "flag-fav" });
  if (item.is_new) flags.push({ icon: Sparkles, label: "New", tid: "flag-new" });
  if (flags.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      {flags.map(({ icon: Icon, label, tid }) => (
        <span
          key={tid}
          data-testid={`${tid}-${item.id || item.name}`}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-[0.6rem] uppercase tracking-[0.18em] border border-primary/40 text-primary/90 bg-primary/5"
        >
          <Icon size={9} strokeWidth={1.5} />
          {label}
        </span>
      ))}
    </div>
  );
}

export default function Menus() {
  const { settings } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swipeHint, setSwipeHint] = useState(false);
  const [activePdfCategory, setActivePdfCategory] = useState("");
  const [pdfCategoryTransitioning, setPdfCategoryTransitioning] = useState(false);
  const [swipeAnim, setSwipeAnim] = useState(null); // "left" | "right" | null
  const [lightbox, setLightbox] = useState(null); // { images: [urls], index: number, name: string } | null
  const [hero, setHero] = useState({});
  const touchStart = useRef(null);
  const lightboxTouchStart = useRef(null);
  const zoomApiRef = useRef(null); // TransformWrapper handle

  // Load the menus hero copy from the CMS so admin can edit it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listSiteContent();
        if (cancelled) return;
        const h = list.find((c) => c.section === "menus-hero");
        if (h) setHero(h);
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const nextLightboxImage = () =>
    setLightbox((lb) => lb && ({ ...lb, index: (lb.index + 1) % lb.images.length }));
  const prevLightboxImage = () =>
    setLightbox((lb) => lb && ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));

  // Close lightbox with ESC + arrow keys + lock body scroll while open
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") nextLightboxImage();
      if (e.key === "ArrowLeft") prevLightboxImage();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [lightbox]);

  // Reset zoom when switching between images inside the lightbox
  useEffect(() => {
    zoomApiRef.current?.resetTransform?.(0);
  }, [lightbox?.index]);

  // Swipe handlers scoped to the lightbox — only navigate when not zoomed in
  const onLightboxTouchStart = (e) => {
    if (!lightbox || lightbox.images.length <= 1) return;
    const scale = zoomApiRef.current?.instance?.transformState?.scale ?? 1;
    if (scale > 1.05) return; // user is panning a zoomed image
    const t = e.changedTouches?.[0];
    if (t) lightboxTouchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };

  const onLightboxTouchEnd = (e) => {
    const start = lightboxTouchStart.current;
    lightboxTouchStart.current = null;
    if (!start || !lightbox || lightbox.images.length <= 1) return;
    const scale = zoomApiRef.current?.instance?.transformState?.scale ?? 1;
    if (scale > 1.05) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 800) {
      if (dx < 0) nextLightboxImage();
      else prevLightboxImage();
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchMenuTree();
        const list = (data && data.length > 0) ? data : SEED_GROUPS.map((g) => ({
          ...g, sections: g.sections.map((s) => ({ ...s, items: s.items }))
        }));
        if (!cancelled) {
          setGroups(list);
          setActiveGroup(list[0]?.id || null);
        }
      } catch {
        if (!cancelled) {
          const list = SEED_GROUPS.map((g) => ({
            ...g, sections: g.sections.map((s) => ({ ...s, items: s.items }))
          }));
          setGroups(list);
          setActiveGroup(list[0]?.id || null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // One-time swipe hint on mobile
  useEffect(() => {
    if (!loading && groups.length > 1 && typeof window !== "undefined" && window.innerWidth < 768) {
      const dismissed = localStorage.getItem("nelum_swipe_hint");
      if (!dismissed) {
        setSwipeHint(true);
        const t = setTimeout(() => {
          setSwipeHint(false);
          localStorage.setItem("nelum_swipe_hint", "1");
        }, 3500);
        return () => clearTimeout(t);
      }
    }
  }, [loading, groups.length]);

  const current = groups.find((g) => g.id === activeGroup) || groups[0] || null;
  const pdfCategories = useMemo(() => {
    const configured = Array.isArray(settings.menu_pdf_categories)
      ? settings.menu_pdf_categories.filter((category) => category?.id && category?.label && category?.pages?.length)
      : [];
    if (configured.length) return configured;
    const legacyPages = Array.isArray(settings.menu_pdf_pages) ? settings.menu_pdf_pages : [];
    return legacyPages.length
      ? [{ id: "food-menu", label: "Food Menu", pdf_url: settings.menu_pdf_url, pages: legacyPages }]
      : [];
  }, [settings.menu_pdf_categories, settings.menu_pdf_pages, settings.menu_pdf_url]);
  const selectedPdfCategory =
    pdfCategories.find((category) => category.id === activePdfCategory) || pdfCategories[0] || null;
  const showPdf = settings.menu_display_mode === "pdf" && Boolean(selectedPdfCategory);

  async function switchPdfCategory(category) {
    if (
      pdfCategoryTransitioning
      || !category
      || category.id === selectedPdfCategory?.id
    ) return;

    setPdfCategoryTransitioning(true);
    const firstPages = (category.pages || []).slice(0, 2);
    await Promise.all(firstPages.map((url) => new Promise((resolve) => {
      const image = new Image();
      image.onload = resolve;
      image.onerror = resolve;
      image.src = absoluteUrl(url);
    })));
    await new Promise((resolve) => setTimeout(resolve, 140));
    setActivePdfCategory(category.id);
    setSearchParams({ category: category.label }, { replace: true });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      setPdfCategoryTransitioning(false);
    }));
  }

  useEffect(() => {
    if (!pdfCategories.length) return;
    const requested = (searchParams.get("category") || "").trim().toLowerCase();
    const requestedCategory = requested
      ? pdfCategories.find((category) => (
        category.id.toLowerCase() === requested
        || category.label.trim().toLowerCase() === requested
        || category.label.trim().toLowerCase().startsWith(requested)
        || requested.startsWith(category.label.trim().toLowerCase())
      ))
      : null;
    const nextCategory = requestedCategory || pdfCategories[0];
    setActivePdfCategory((currentCategory) => {
      if (
        (requestedCategory && requestedCategory.id !== currentCategory)
        || !pdfCategories.some((category) => category.id === currentCategory)
      ) {
        return nextCategory.id;
      }
      return currentCategory;
    });
  }, [pdfCategories, searchParams]);
  const currentIdx = useMemo(
    () => groups.findIndex((g) => g.id === activeGroup),
    [groups, activeGroup],
  );

  function goToGroup(direction) {
    if (groups.length < 2) return;
    const next = (currentIdx + direction + groups.length) % groups.length;
    setSwipeAnim(direction > 0 ? "left" : "right");
    setActiveGroup(groups[next].id);
    window.scrollTo({
      top: (document.getElementById("menu-group-body")?.offsetTop || 0) - 120,
      behavior: "smooth",
    });
    setTimeout(() => setSwipeAnim(null), 450);
  }

  function onTouchStart(e) {
    const t = e.changedTouches?.[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  }
  function onTouchEnd(e) {
    const start = touchStart.current;
    if (!start) return;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const dt = Date.now() - start.time;
    touchStart.current = null;
    // Only treat as horizontal swipe if fast enough and mostly horizontal
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 700) {
      goToGroup(dx < 0 ? 1 : -1);
    }
  }

  return (
    <div data-testid="page-menus" className="touch-pan-y">
      {/* Header */}
      {settings.menu_hero_visible !== false && (
        <section className="relative border-b border-border min-h-[70vh] flex items-center">
          <HeroSlideshow page="menus" />
          <div className="relative z-10 w-full mx-auto max-w-[1400px] px-6 lg:px-12 py-32 lg:py-40 fade-up">
            <div className="overline">{hero.overline || "The Menu"}</div>
            {hero.title_visible !== false && (
              <h1
                data-testid="menus-hero-title"
                className="mt-8 serif text-5xl md:text-7xl lg:text-8xl font-light leading-[0.95] tracking-tighter text-foreground max-w-4xl [text-shadow:0_2px_20px_rgba(0,0,0,0.55)]"
              >
                {hero.title_top || "An island,"} <br />
                <span className="italic text-foreground">
                  {hero.title_italic || "served slowly."}
                </span>
              </h1>
            )}
            {hero.body_visible !== false && (hero.body || !hero.title_top) && (
              <p
                data-testid="menus-hero-body"
                className="mt-8 max-w-xl text-sm md:text-base leading-relaxed text-foreground/90 [text-shadow:0_1px_10px_rgba(0,0,0,0.5)] whitespace-pre-line"
              >
                {hero.body ||
                  "Menus rotate with the season and the shore. Below is our current evening service — prices in thousands of Rupiah (IDR)."}
              </p>
            )}
          </div>
        </section>
      )}

      {showPdf ? (
        <>
          {pdfCategories.length > 1 && (
            <section
              aria-label="PDF menu categories"
              className={`relative z-40 min-h-[5rem] border-b border-border bg-background/95 backdrop-blur-xl ${
                settings.menu_hero_visible === false ? "pt-24 md:pt-28 lg:pt-32" : ""
              }`}
            >
              <div className="mx-auto flex min-h-[5rem] max-w-[1100px] items-end px-4 sm:px-6 lg:px-12">
                <div className="flex items-stretch justify-center overflow-x-auto border-b border-border">
                  {pdfCategories.map((category) => {
                    const active = category.id === selectedPdfCategory.id;
                    return (
                      <button
                        key={category.id}
                        type="button"
                        data-testid={`menu-pdf-category-${category.id}`}
                        aria-pressed={active}
                        onClick={() => switchPdfCategory(category)}
                        disabled={pdfCategoryTransitioning}
                        className={`relative shrink-0 px-7 py-4 text-[0.68rem] uppercase tracking-[0.2em] transition-colors ${
                          active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                        } disabled:cursor-wait`}
                      >
                        {category.label}
                        {active && <span className="absolute inset-x-3 bottom-0 h-px bg-primary" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
          <div
            className={`transition-[opacity,transform] duration-300 ease-out ${
              pdfCategoryTransitioning
                ? "opacity-0 translate-y-1"
                : "opacity-100 translate-y-0"
            }`}
          >
            <MenuPdfBook
              key={selectedPdfCategory.id}
              pages={selectedPdfCategory.pages}
              pdfUrl={selectedPdfCategory.pdf_url}
              hasCategoryTabs={pdfCategories.length > 1}
            />
          </div>
        </>
      ) : (
        <>
      {/* ============ Group Tabs ============ */}
      {groups.length > 0 && (
        <section
          aria-label="menu-group-tabs"
          className="sticky top-24 md:top-28 lg:top-32 z-30 border-b border-border bg-background/90 backdrop-blur-xl"
        >
          <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
            <div className="flex items-stretch overflow-x-auto">
              {groups.map((g) => {
                const Icon = GROUP_ICONS[g.id] || iconForGroup(g.label);
                const active = g.id === activeGroup;
                return (
                  <button
                    key={g.id}
                    data-testid={`menu-group-tab-${g.id}`}
                    onClick={() => {
                      setActiveGroup(g.id);
                      window.scrollTo({
                        top: (document.getElementById("menu-group-body")?.offsetTop || 0) - 120,
                        behavior: "smooth",
                      });
                    }}
                    className={`relative flex-1 min-w-[140px] flex items-center justify-center gap-3 py-6 md:py-8 text-[0.72rem] uppercase tracking-[0.22em] font-semibold transition-colors duration-300 border-b-2 ${
                      active
                        ? "text-primary border-primary"
                        : "text-foreground/60 border-transparent hover:text-foreground"
                    }`}
                  >
                    <Icon size={14} strokeWidth={1.5} />
                    {g.label}
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ============ Group Body ============ */}
      <div
        id="menu-group-body"
        key={current?.id || "empty"}
        data-testid={current ? `menu-group-body-${current.id}` : "menu-group-body-empty"}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className={`mx-auto max-w-[1400px] px-6 lg:px-12 py-16 lg:py-24 fade-up touch-pan-y ${
          swipeAnim === "left" ? "animate-[slideInLeft_450ms_ease-out]" : ""
        } ${swipeAnim === "right" ? "animate-[slideInRight_450ms_ease-out]" : ""}`}
      >
        {/* Mobile swipe hint */}
        {swipeHint && current && (
          <div
            data-testid="menu-swipe-hint"
            className="md:hidden fixed inset-x-0 bottom-8 z-40 flex justify-center pointer-events-none animate-[fadeIn_400ms_ease-out]"
          >
            <div className="inline-flex items-center gap-3 px-5 py-3 bg-card/90 backdrop-blur-md border border-primary/40 text-primary text-[0.65rem] uppercase tracking-[0.22em]">
              <ChevronLeft size={12} strokeWidth={1.5} className="animate-pulse" />
              Swipe to change category
              <ChevronRight size={12} strokeWidth={1.5} className="animate-pulse" />
            </div>
          </div>
        )}

        {/* Mobile page indicator + arrows below title */}
        {current && groups.length > 1 && (
          <div className="md:hidden flex items-center justify-between mb-8">
            <button
              data-testid="menu-prev-group"
              onClick={() => goToGroup(-1)}
              aria-label="Previous category"
              className="p-2 border border-border text-foreground/80 hover:border-primary hover:text-primary transition-colors"
            >
              <ChevronLeft size={14} strokeWidth={1.5} />
            </button>
            <div className="flex items-center gap-2">
              {groups.map((g, i) => (
                <span
                  key={g.id}
                  className={`h-[3px] transition-all duration-300 ${
                    i === currentIdx ? "w-6 bg-primary" : "w-3 bg-foreground/30"
                  }`}
                />
              ))}
            </div>
            <button
              data-testid="menu-next-group"
              onClick={() => goToGroup(1)}
              aria-label="Next category"
              className="p-2 border border-border text-foreground/80 hover:border-primary hover:text-primary transition-colors"
            >
              <ChevronRight size={14} strokeWidth={1.5} />
            </button>
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center text-muted-foreground italic serif text-xl">
            <span className="ornament">Loading menu</span>
          </div>
        ) : !current ? (
          <div className="py-24 text-center text-muted-foreground">
            No menu published yet. Please check back soon.
          </div>
        ) : (
          <>
            <div className="mb-14 md:mb-20 max-w-2xl">
              <div className="overline">{current.label}</div>
              <div className="mt-4 serif italic text-2xl md:text-3xl text-foreground/90 leading-tight">
                {current.tagline}
              </div>
            </div>

            {/* In-group section quick-nav */}
            {current.sections.length > 0 && (
              <div className="mb-14 flex items-center gap-x-8 gap-y-3 flex-wrap border-y border-border py-4">
                {current.sections.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    data-testid={`menu-nav-${sec.id}`}
                    className="link-underline text-[0.72rem] uppercase tracking-[0.22em] text-foreground/70 hover:text-primary transition-colors"
                  >
                    {sec.title}
                  </a>
                ))}
              </div>
            )}

            {/* Sections */}
            <div className="space-y-24 lg:space-y-32">
              {current.sections.map((sec, idx) => (
                <section
                  key={sec.id}
                  id={sec.id}
                  data-testid={`menu-section-${sec.id}`}
                  className="grid lg:grid-cols-12 gap-10 lg:gap-16 scroll-mt-48"
                >
                  <div className="lg:col-span-4">
                    <div className="overline">
                      {String(idx + 1).padStart(2, "0")} — {sec.title}
                    </div>
                    <h2 className="mt-6 serif text-3xl md:text-5xl font-light leading-tight text-foreground">
                      {sec.title}
                    </h2>
                    {sec.subtitle && (
                      <p className="mt-6 text-sm text-muted-foreground italic leading-relaxed">
                        {sec.subtitle}
                      </p>
                    )}
                  </div>

                  <div className="lg:col-span-8 space-y-8">
                    {sec.items.map((it) => {
                      const itemImages = (it.images && it.images.length > 0)
                        ? it.images
                        : (it.image_url ? [it.image_url] : []);
                      return (
                      <div
                        key={it.id || it.name}
                        data-testid={`menu-item-${(it.name || "").toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, "")}`}
                        className="group grid grid-cols-[1fr_auto] gap-6 pb-8 border-b border-border last:border-b-0 transition-colors duration-300 hover:border-primary/50"
                      >
                        <div className="flex gap-4 sm:gap-5">
                          {itemImages.length > 0 && (
                            <button
                              type="button"
                              onClick={() =>
                                setLightbox({ images: itemImages, index: 0, name: it.name })
                              }
                              data-testid={`menu-item-image-btn-${it.id || it.name}`}
                              aria-label={`View ${it.name} images`}
                              className="relative w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 shrink-0 overflow-hidden border border-white/5 bg-black/40 focus:outline-none focus:ring-2 focus:ring-primary/60"
                            >
                              <img
                                src={absoluteUrl(itemImages[0])}
                                alt={it.name}
                                loading="lazy"
                                className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                                data-testid={`menu-item-image-${it.id || it.name}`}
                              />
                              {itemImages.length > 1 && (
                                <span className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-background/85 backdrop-blur-sm text-[0.55rem] uppercase tracking-[0.14em] font-semibold text-foreground/90">
                                  <Sparkles size={8} strokeWidth={1.8} />
                                  {itemImages.length}
                                </span>
                              )}
                            </button>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="serif text-xl md:text-2xl text-foreground group-hover:text-primary transition-colors duration-300">
                              {it.name}
                            </div>
                            {it.desc && (
                              <div className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                                {it.desc}
                              </div>
                            )}
                            <ItemFlags item={it} />
                          </div>
                        </div>
                        <div className="serif text-lg md:text-xl text-primary tabular-nums self-start pt-1 whitespace-nowrap">
                          {it.price}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            {/* Closing note */}
            <section className="mt-24 border-t border-border pt-14 text-center">
              <div className="serif italic text-2xl md:text-3xl text-foreground/85">
                <span className="ornament">A table shared is a story told</span>
              </div>
              <p className="mt-6 text-sm text-muted-foreground max-w-lg mx-auto">
                {current.id === "wine"
                  ? "Prices shown as glass / bottle. All bottles subject to availability. Corkage on request."
                  : "All prices exclude 10% service and 11% VAT. Please inform us of allergies — we happily adapt where the kitchen allows."}
              </p>
              <Link
                to="/reservation"
                data-testid="menus-cta-reserve"
                className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 transition-colors group"
              >
                Reserve a Table
                <ArrowRight
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </section>
          </>
        )}
      </div>
        </>
      )}

      {/* Lightbox — pinch-zoom + swipe navigation */}
      {!showPdf && lightbox && (
        <div
          data-testid="menu-lightbox"
          onClick={() => setLightbox(null)}
          onTouchStart={onLightboxTouchStart}
          onTouchEnd={onLightboxTouchEnd}
          role="dialog"
          aria-modal="true"
          aria-label={`${lightbox.name} — image ${lightbox.index + 1} of ${lightbox.images.length}`}
          className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-xl p-4 md:p-10 animate-[fadeIn_200ms_ease-out] touch-pan-y"
        >
          <button
            type="button"
            data-testid="menu-lightbox-close"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            aria-label="Close"
            className="absolute top-5 right-5 md:top-8 md:right-8 w-10 h-10 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors z-20"
          >
            <X size={16} strokeWidth={1.5} />
          </button>

          {lightbox.images.length > 1 && (
            <>
              <button
                type="button"
                data-testid="menu-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); prevLightboxImage(); }}
                aria-label="Previous image"
                className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors z-20"
              >
                <ChevronLeft size={18} strokeWidth={1.5} />
              </button>
              <button
                type="button"
                data-testid="menu-lightbox-next"
                onClick={(e) => { e.stopPropagation(); nextLightboxImage(); }}
                aria-label="Next image"
                className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors z-20"
              >
                <ChevronRight size={18} strokeWidth={1.5} />
              </button>
            </>
          )}

          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full flex flex-col items-center gap-4"
          >
            <TransformWrapper
              key={lightbox.index}
              initialScale={1}
              minScale={1}
              maxScale={5}
              centerOnInit
              doubleClick={{ disabled: false, mode: "toggle", step: 2 }}
              wheel={{ step: 0.1 }}
              pinch={{ step: 5 }}
              panning={{ velocityDisabled: true }}
              ref={zoomApiRef}
            >
              {({ zoomIn, zoomOut, resetTransform }) => (
                <>
                  <TransformComponent
                    wrapperClass="!w-full !h-full flex items-center justify-center"
                    contentClass="!w-full !h-full flex items-center justify-center"
                  >
                    <img
                      src={absoluteUrl(lightbox.images[lightbox.index])}
                      alt={lightbox.name}
                      draggable={false}
                      className="max-h-[75vh] w-auto max-w-full object-contain border border-border shadow-[0_20px_60px_rgba(0,0,0,0.6)] select-none"
                      data-testid="menu-lightbox-image"
                    />
                  </TransformComponent>

                  {/* Desktop zoom controls — hidden on mobile since pinch is natural */}
                  <div className="hidden md:flex absolute bottom-4 right-4 items-center gap-1 z-20">
                    <button
                      type="button"
                      data-testid="menu-lightbox-zoom-in"
                      onClick={(e) => { e.stopPropagation(); zoomIn(); }}
                      aria-label="Zoom in"
                      className="w-10 h-10 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <ZoomIn size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      data-testid="menu-lightbox-zoom-out"
                      onClick={(e) => { e.stopPropagation(); zoomOut(); }}
                      aria-label="Zoom out"
                      className="w-10 h-10 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      <ZoomOut size={14} strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      data-testid="menu-lightbox-zoom-reset"
                      onClick={(e) => { e.stopPropagation(); resetTransform(); }}
                      aria-label="Reset zoom"
                      className="w-10 h-10 flex items-center justify-center border border-border bg-background/60 text-foreground hover:border-primary hover:text-primary transition-colors text-[0.6rem] uppercase tracking-[0.16em] font-semibold"
                    >
                      1×
                    </button>
                  </div>
                </>
              )}
            </TransformWrapper>

            <figcaption className="flex flex-col items-center gap-2 relative z-10">
              {lightbox.name && (
                <div
                  className="serif italic text-lg md:text-xl text-foreground/85 text-center"
                  data-testid="menu-lightbox-caption"
                >
                  {lightbox.name}
                </div>
              )}
              {lightbox.images.length > 1 && (
                <>
                  <div
                    data-testid="menu-lightbox-counter"
                    className="text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground tabular-nums"
                  >
                    {lightbox.index + 1} · {lightbox.images.length}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1">
                    {lightbox.images.map((imgUrl, i) => (
                      <button
                        key={`lightbox-dot-${imgUrl || i}`}
                        data-testid={`menu-lightbox-dot-${i}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setLightbox((lb) => lb && ({ ...lb, index: i }));
                        }}
                        aria-label={`Go to image ${i + 1}`}
                        className={`transition-all duration-300 h-1 rounded-full ${
                          i === lightbox.index ? "w-6 bg-primary" : "w-1.5 bg-foreground/30 hover:bg-foreground/60"
                        }`}
                      />
                    ))}
                  </div>
                </>
              )}
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-muted-foreground/70 italic mt-1 hidden sm:block">
                Pinch to zoom · Double-tap to toggle · Swipe to navigate
              </div>
            </figcaption>
          </div>
        </div>
      )}
    </div>
  );
}
